import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../../services/api';

interface ChypsIntakeProps {
  embedded?: boolean;
}

const ChypsIntake: React.FC<ChypsIntakeProps> = ({ embedded = false }) => {
  const [upid, setUpid] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [linkError, setLinkError] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [links, setLinks] = useState<Array<{ id: number; token: string; upid: string; created_at: string; status: string; submitted_at: string | null }>>([]);
  const [responses, setResponses] = useState<Array<{ id: number; upid: string; submitted_at: string; scores: Record<string, unknown>; link_id: number }>>([]);
  const [listLoading, setListLoading] = useState(false);
  const [pdfLoadingId, setPdfLoadingId] = useState<number | null>(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [removingLinkId, setRemovingLinkId] = useState<number | null>(null);

  const loadLists = () => {
    setListLoading(true);
    Promise.all([api.chypsListLinks(), api.chypsListResponses()])
      .then(([linksRes, responsesRes]) => {
        setLinks(linksRes.links || []);
        setResponses(responsesRes.responses || []);
      })
      .catch(() => {})
      .finally(() => setListLoading(false));
  };

  useEffect(() => {
    loadLists();
  }, []);

  const onGenerateLink = () => {
    const trimmed = upid.trim();
    if (!trimmed) return;
    setLinkError('');
    setGeneratedUrl(null);
    setLinkLoading(true);
    api
      .chypsCreateLink(trimmed)
      .then((data) => {
        setGeneratedUrl(data.url);
      })
      .catch((e: { data?: { error?: string }; message?: string }) => {
        setLinkError(e?.data?.error || e?.message || 'Failed to generate link');
      })
      .finally(() => {
        setLinkLoading(false);
        loadLists();
      });
  };

  const copyUrl = () => {
    if (generatedUrl) {
      navigator.clipboard.writeText(generatedUrl);
    }
  };

  const onDownloadPdf = (responseId: number) => {
    setPdfLoadingId(responseId);
    api
      .chypsDownloadResponsePdf(responseId)
      .catch(() => {})
      .finally(() => setPdfLoadingId(null));
  };

  const onRemoveLink = (linkId: number) => {
    if (removingLinkId !== null) return;
    setRemovingLinkId(linkId);
    api
      .chypsDeleteLink(linkId)
      .then(() => loadLists())
      .catch(() => {})
      .finally(() => setRemovingLinkId(null));
  };

  const onExportCsv = () => {
    setCsvLoading(true);
    api
      .chypsDownloadExportCsv()
      .catch(() => {})
      .finally(() => setCsvLoading(false));
  };

  return (
    <div style={{ padding: embedded ? 0 : 24, maxWidth: 900 }}>
      {!embedded && (
        <Link to="/admin/portal" style={{ display: 'inline-block', marginBottom: 16 }}>
          Back to home
        </Link>
      )}
      {!embedded && <h1>Symptoms Profile intake</h1>}
      <p style={{ color: '#666', marginBottom: 24 }}>
        Generate a secure link for a patient to fill out the Symptoms Profile questionnaire. Send the link by email; after they submit, you can print PDF or export CSV.
      </p>

      <section style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 18, marginBottom: 12 }}>Generate patient link</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Patient ID (e.g. CHY111)"
            value={upid}
            onChange={(e) => setUpid(e.target.value)}
            style={{ padding: 8, width: 200 }}
          />
          <button type="button" onClick={onGenerateLink} disabled={linkLoading}>
            {linkLoading ? 'Generating…' : 'Generate link'}
          </button>
        </div>
        {linkError && <p style={{ color: '#c00', marginTop: 8 }}>{linkError}</p>}
        {generatedUrl && (
          <div style={{ marginTop: 12 }}>
            <p style={{ fontWeight: 500, marginBottom: 4 }}>Link (copy and send to patient):</p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="text" readOnly value={generatedUrl} style={{ flex: 1, padding: 8 }} />
              <button type="button" onClick={copyUrl}>Copy</button>
            </div>
          </div>
        )}
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 18, marginBottom: 12 }}>Links</h2>
        {listLoading ? (
          <p>Loading…</p>
        ) : links.length === 0 ? (
          <p style={{ color: '#666' }}>No links yet. Generate a link above.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                <th style={{ padding: 8 }}>UPID</th>
                <th style={{ padding: 8 }}>Created</th>
                <th style={{ padding: 8 }}>Status</th>
                <th style={{ padding: 8 }}>Submitted</th>
                <th style={{ padding: 8 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {links.map((l) => (
                <tr key={l.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 8 }}>{l.upid}</td>
                  <td style={{ padding: 8 }}>{new Date(l.created_at).toLocaleString()}</td>
                  <td style={{ padding: 8 }}>{l.status}</td>
                  <td style={{ padding: 8 }}>{l.submitted_at ? new Date(l.submitted_at).toLocaleString() : '—'}</td>
                  <td style={{ padding: 8 }}>
                    <button
                      type="button"
                      onClick={() => onRemoveLink(l.id)}
                      disabled={removingLinkId === l.id}
                      style={{ color: '#c00' }}
                    >
                      {removingLinkId === l.id ? '…' : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 18, marginBottom: 12 }}>Responses</h2>
        <div style={{ marginBottom: 12 }}>
          <button type="button" onClick={onExportCsv} disabled={csvLoading}>
            {csvLoading ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
        {listLoading ? (
          <p>Loading…</p>
        ) : responses.length === 0 ? (
          <p style={{ color: '#666' }}>No submitted responses yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                <th style={{ padding: 8 }}>UPID</th>
                <th style={{ padding: 8 }}>Submitted</th>
                <th style={{ padding: 8 }}>Total score</th>
                <th style={{ padding: 8 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {responses.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: 8 }}>{r.upid}</td>
                  <td style={{ padding: 8 }}>{new Date(r.submitted_at).toLocaleString()}</td>
                  <td style={{ padding: 8 }}>{(r.scores as { total_score?: number })?.total_score ?? '—'}</td>
                  <td style={{ padding: 8 }}>
                    <button
                      type="button"
                      onClick={() => onDownloadPdf(r.id)}
                      disabled={pdfLoadingId === r.id}
                    >
                      {pdfLoadingId === r.id ? '…' : 'Print PDF'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default ChypsIntake;
